import { gql, useLazyQuery, useQuery } from "@apollo/client";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

import type { IProductDetailQuery, IProductDetailRecommendQuery, IProductDetailVariants } from "@repo/shopify/queries/product-gql";
import { productDetailQuery, productDetailRecommendQuery } from "@repo/shopify/queries/product-gql";
import useCustomerQueryCaa from "@repo/shopify/queries/useCustomerQueryCaa";
import { extractId } from "@repo/shopify/utils/common";

import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import ProductCard from "@molecules/ProductCard";

import ProductInformation from "@organisms/ProductInformation";

import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCheckLocation from "@hooks/useCheckLocation";
import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import apolloCaaClient from "@lib/apolloCaa";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const RECENTLY_VIEWED = "TINT_RECENTLY_VIEWED";

export default function Products() {
  const navigate = useNavigate();
  const { handle } = useParams();

  const { lang } = useLanguage();
  const isJp = lang === "jp";

  const { token, customer, caaToken, caaCustomer, updateCaa } = useCustomerStore();
  const { openToast, setMeta, showFooter } = useLayoutStore();

  const { isDanielJohnston } = useCheckLocation();
  const { fetchCustomer, fetchCustomerLoading } = useCustomerQuery();
  const { fetchCaaCustomer, fetchCaaCustomerLoading } = useCustomerQueryCaa({ client: apolloCaaClient });

  const [showProducts, setShowProducts] = useState(false);

  useEffect(() => {
    showFooter(true);
    window.history.replaceState({}, "");
  }, []);

  useEffect(() => {
    if (isJp) {
      if (!caaToken?.accessToken) return;
      void fetchCaaCustomer().then(({ data }) => {
        if (data?.customer) updateCaa(data.customer);
      });
      return;
    }

    if (token.accessToken) {
      void fetchCustomer({ variables: { customerAccessToken: token.accessToken } });
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [handle]);

  const { data, loading } = useQuery<IProductDetailQuery>(gql(productDetailQuery.query), {
    variables: { handle },
    onCompleted: (_data) => {
      if (!_data.product) {
        navigate(URLS.NOT_FOUND);
        return;
      }

      if (_data.product?.hidden?.value === "true") {
        const customerTags = (isJp ? caaCustomer?.tags : customer.tags) ?? [];
        const isStaff = customerTags.includes("Staff");
        const isOnlyEmployee =
          customerTags.includes("Employee") &&
          (_data.product?.employeeOnly?.value === "true" || _data.product?.tags.includes("Only Employee"));

        if (!isStaff && !isOnlyEmployee) {
          navigate(URLS.NOT_FOUND);
          return;
        }
      }

      setShowProducts(true);
      void getRecommend({ variables: { productId: _data.product?.id } });
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    if (!isDanielJohnston) return;

    document.body.setAttribute("class", "dark");
    document.body.style.background = "#2b2b2b";

    return () => {
      document.body.setAttribute("class", "white");
      document.body.style.background = "";
    };
  }, [isDanielJohnston]);

  const [getRecommend, { data: recommendations }] = useLazyQuery<IProductDetailRecommendQuery>(gql(productDetailRecommendQuery.query), {
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    if (data?.product?.employeeOnly?.value === "true" || data?.product?.tags.includes("Only Employee")) return;
    setMeta({
      title: `${data?.product ? `${data?.product?.title} - ` : ""} ${DEFAULT_TITLE}`,
      description: data?.product?.description || DEFAULT_DESCRIPTION,
    });
  }, [data]);

  const recentlyProductList = useMemo(() => {
    if (!data?.product || data?.product?.hidden?.value === "true") return [];

    const localData = JSON.parse(localStorage.getItem(RECENTLY_VIEWED) ?? "[]") as IProductDetailQuery["product"][];

    // 기존 목록에서 현재 상품 제거
    const filteredList = localData.filter((prod) => prod?.id !== data?.product?.id);

    // 현재 상품을 맨 앞에 추가
    const newRecentList = [data?.product, ...filteredList].slice(0, 7);
    localStorage.setItem(RECENTLY_VIEWED, JSON.stringify(newRecentList));

    return newRecentList.filter((prod) => prod?.id !== data?.product?.id).slice(0, 6);
  }, [data?.product]);

  const relatedProductList: IProductDetailQuery["product"][] = useMemo(
    () =>
      data?.product
        ? (recommendations?.productRecommendations.filter(
            (productItem) =>
              productItem?.availableForSale ||
              Boolean(productItem?.release_date?.value && new Date(productItem.release_date.value) > new Date()),
          ) ?? [])
        : [],
    [recommendations, data?.product],
  );

  const jsonLd = useMemo(() => {
    if (!data?.product) return null;
    const { title, description, featuredImage, options, variants } = data.product;

    const optionToSchema: Record<string, string> = {
      Size: "https://schema.org/size",
      Color: "https://schema.org/color",
    };

    const variesBy = options.filter((option) => optionToSchema[option.name]).map((option) => optionToSchema[option.name]);

    const hasVariant = variants.edges.map(({ node }) => {
      const color = node.selectedOptions.find((option) => option.name === "Color")?.value;
      const size = node.selectedOptions.find((option) => option.name === "Size")?.value;
      return {
        "@context": "https://schema.org/",
        "@type": "Product",
        name: `${title} - ${node.title}`,
        sku: node.sku,
        image: node.image?.url ?? featuredImage?.url,
        ...(color && { color }),
        ...(size && { size }),
        brand: { "@type": "Brand", name: "thisisneverthat", alternateName: "디스이즈네버댓" },
        offers: {
          "@type": "Offer",
          url: window.location.href,
          priceCurrency: node.price.currencyCode,
          price: node.price.amount,
          availability: node.availableForSale ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        },
      };
    });

    return {
      "@context": "https://schema.org/",
      "@type": "ProductGroup",
      productGroupId: extractId(data.product.id),
      name: title,
      description,
      image: featuredImage?.url,
      brand: { "@type": "Brand", name: "thisisneverthat", alternateName: "디스이즈네버댓" },
      ...(variesBy.length && { variesBy }),
      hasVariant,
    };
  }, [data?.product]);

  return (
    <>
      <Helmet>
        <meta content="product" property="og:type" />
        {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd)}</script> : null}
      </Helmet>

      {(!showProducts && !data && loading) || fetchCustomerLoading || fetchCaaCustomerLoading ? (
        <Loading type="page" />
      ) : (
        <>
          {showProducts && data ? (
            <>
              <ProductInformation product={data.product} />
              <div className="pc:col-span-12 pc:gap-y-20 pc:pt-20 col-span-8 flex flex-col gap-y-20 pt-20 pb-10">
                <section className="flex flex-col gap-2">
                  {relatedProductList.length ? (
                    <>
                      <div className="flex justify-between text-base!">
                        <h2 className={classNames(isDanielJohnston && "font-daily")}>Related Products</h2>
                        <Link
                          as="text"
                          className={classNames(isDanielJohnston && "font-fuzzy font-fuzzy-bold")}
                          href={`${URLS.COLLECTIONS_DETAIL}/${data?.product?.collections?.edges?.[0]?.node?.handle || "all"}`}
                          isActive={false}
                        >
                          View All
                        </Link>
                      </div>
                      <section className="pc:tnt-gap-x pc:col-span-12 pc:grid-cols-6 pc:gap-y-15 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                        {relatedProductList.slice(0, 6).map((node) => (
                          <ProductCard
                            color={node?.options.find((item) => item.name === "Color")?.values || []}
                            key={node?.id}
                            releaseDate={node?.release_date}
                            tags={node?.tags}
                            title={node?.title || ""}
                            variants={node?.variants as IProductDetailVariants}
                          />
                        ))}
                      </section>
                    </>
                  ) : null}
                </section>
                <section className="flex flex-col gap-2">
                  {recentlyProductList.length ? (
                    <>
                      <div className="flex justify-between text-base!">
                        <h2 className={classNames(isDanielJohnston && "font-daily")}>Recently Viewed</h2>
                      </div>
                      <section className="pc:tnt-gap-x pc:col-span-12 pc:grid-cols-6 pc:gap-y-15 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                        {recentlyProductList.slice(0, 6).map((node) => (
                          <ProductCard
                            color={node?.options.find((item) => item.name === "Color")?.values || []}
                            key={node?.id}
                            releaseDate={node?.release_date}
                            tags={node?.tags}
                            title={node?.title || ""}
                            variants={node?.variants as IProductDetailVariants}
                          />
                        ))}
                      </section>
                    </>
                  ) : null}
                </section>
              </div>
            </>
          ) : null}
        </>
      )}
    </>
  );
}
